import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { useOrganizationStore } from "../../store/organization";
import { useEventStore } from "../../store/event";
import { userStore } from "../../store/user";
import { Attendee } from "../../types/attendee";
import { GET_ATTENDEES, GET_ATTENDEES_ALL, GET_ATTENDEES_STATS } from "./queries";
import { UPDATE_ATTENDEE } from "./mutations";
import { usePermissions } from "../auth/api";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import Loader from "../../components/Loader";
import { AttendeesTable, SortDir, SortField } from "./AttendeesTable";
import { exportAttendeesCsv } from "./exportAttendeesCsv";
import { CardFilter, HAS_ACTIVE_CARD_WHERE, applyCardFilter, parseCardFilter } from "./cardOnFile";
import { AttendeeTypeFilter, applyAttendeeTypeFilter, parseAttendeeTypeFilter } from "./attendeeType";

const client = createApolloClient();

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_FROM = moment().subtract(90, "days").startOf("day").format("YYYY-MM-DD");
const DEFAULT_TO = moment().add(1, "day").startOf("day").format("YYYY-MM-DD");

const buildOrderBy = (field: SortField, dir: SortDir): any[] => {
  switch (field) {
    case "id":
      return [{ id: dir }];
    case "name":
      return [{ first_name: dir }, { last_name: dir }];
    case "event":
      return [{ event: { name: dir } }];
    case "validation_code":
      return [{ validation_code: dir }];
    case "email":
      return [{ email: dir }];
    case "phone":
      return [{ phone_number: dir }];
    case "created_at":
    default:
      return [{ created_at: dir }];
  }
};

const escapeIlike = (s: string) => s.replace(/[%_\\]/g, (m) => `\\${m}`);

export const AttendeeList = () => {
  const { editCreatePermission } = usePermissions("attendees");
  const navigate = useNavigate();
  const location = useLocation();
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  // Counts over the base filters (before the card toggle), used for pagination and the stat line.
  const [allCount, setAllCount] = useState(0);
  const [withCardCount, setWithCardCount] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [bulkPending, setBulkPending] = useState(false);

  const [query, setQuery] = useState(() => new URLSearchParams(location.search).get("search") ?? "");
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [dateFrom, setDateFrom] = useState<string>(() => new URLSearchParams(location.search).get("from") ?? DEFAULT_FROM);
  const [dateTo, setDateTo] = useState<string>(() => new URLSearchParams(location.search).get("to") ?? DEFAULT_TO);
  const [cardFilter, setCardFilter] = useState<CardFilter>(() => parseCardFilter(new URLSearchParams(location.search).get("card")));
  const [typeFilter, setTypeFilter] = useState<AttendeeTypeFilter>(() => parseAttendeeTypeFilter(new URLSearchParams(location.search).get("type")));
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const headerOrgId = useOrganizationStore((s) => s.organizationId);
  const headerEventId = useEventStore((s) => s.eventId);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, dateFrom, dateTo, cardFilter, typeFilter, sortField, sortDir, headerOrgId, headerEventId]);

  // Everything except the card toggle. The stat line ("X of Y have a card on file") is always over this set,
  // so with the Ticketing / Cashless toggle on it reads as a share of that attendee type.
  const baseWhere = useMemo(() => {
    const organizationId = parseInt(headerOrgId !== null ? headerOrgId.toString() : "", 10);
    const eventId = headerEventId;
    const user = userStore.getState().user;

    const w: any = {};
    if (organizationId && !Number.isNaN(organizationId) && organizationId !== 0) {
      w.event = { organization_id: { _eq: organizationId } };
    }
    if (eventId) {
      w.event_id = { _eq: eventId };
    } else if (user?.events?.length) {
      w.event_id = { _in: user.events };
    }
    if (dateFrom || dateTo) {
      w.created_at = {
        ...(dateFrom ? { _gte: moment(dateFrom).startOf("day").toISOString() } : {}),
        ...(dateTo ? { _lt: moment(dateTo).startOf("day").toISOString() } : {}),
      };
    }
    const q = debouncedQuery.trim();
    if (q) {
      // Each whitespace-separated word must match some field on its own, so
      // "John Smith" finds first_name John + last_name Smith. Matching the whole
      // string against single columns dropped the person as soon as a space
      // was typed. A single word keeps the id match too.
      const termWhere = (term: string): any => {
        const like = `%${escapeIlike(term)}%`;
        const ors: any[] = [
          { first_name: { _ilike: like } },
          { last_name: { _ilike: like } },
          { email: { _ilike: like } },
          { phone_number: { _ilike: like } },
          { validation_code: { _ilike: like } },
          { event: { name: { _ilike: like } } },
        ];
        const asInt = Number.parseInt(term, 10);
        if (!Number.isNaN(asInt)) ors.push({ id: { _eq: asInt } });
        return { _or: ors };
      };
      const terms = q.split(/\s+/).filter(Boolean);
      if (terms.length === 1) {
        w._or = termWhere(terms[0])._or;
      } else {
        w._and = terms.map(termWhere);
      }
    }
    return applyAttendeeTypeFilter(w, typeFilter);
  }, [debouncedQuery, dateFrom, dateTo, typeFilter, headerOrgId, headerEventId]);

  const where = useMemo(() => applyCardFilter(baseWhere, cardFilter), [baseWhere, cardFilter]);

  const orderBy = useMemo(() => buildOrderBy(sortField, sortDir), [sortField, sortDir]);

  const fetchPage = useCallback(async () => {
    setIsLoading(true);
    try {
      const [{ data: pageData }, { data: statsData }] = await Promise.all([
        client.query({
          query: GET_ATTENDEES,
          variables: { where, order_by: orderBy, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_ATTENDEES_STATS,
          variables: { where: baseWhere, withCard: { _and: [baseWhere, HAS_ACTIVE_CARD_WHERE] } },
          fetchPolicy: "network-only",
        }),
      ]);
      if (!mountedRef.current) return;
      setAttendees(pageData?.attendees ?? []);
      setAllCount(statsData?.all?.aggregate?.count ?? 0);
      setWithCardCount(statsData?.with_card?.aggregate?.count ?? 0);
    } catch (error) {
      console.error("Error fetching attendees:", error);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [where, baseWhere, orderBy, page]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleBulkSetInactive = useCallback(
    async (ids: number[]) => {
      if (ids.length === 0) return;
      setBulkPending(true);
      try {
        await client.mutate({
          mutation: UPDATE_ATTENDEE,
          variables: {
            input: { is_active: false },
            where: { id: { _in: ids } },
          },
        });
        toast.success(`Set ${ids.length} attendee${ids.length === 1 ? "" : "s"} inactive`);
        await fetchPage();
      } catch (err: any) {
        console.error("Bulk set inactive failed:", err);
        toast.error(err?.message ?? "Failed to update attendees");
      } finally {
        if (mountedRef.current) setBulkPending(false);
      }
    },
    [fetchPage]
  );

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const { data } = await client.query({
        query: GET_ATTENDEES_ALL,
        variables: { where, order_by: orderBy },
        fetchPolicy: "network-only",
      });
      const rows = (data?.attendees ?? []) as Attendee[];
      exportAttendeesCsv(rows);
    } catch (err: any) {
      console.error("Export failed:", err);
      toast.error(err?.message ?? "Failed to export attendees");
    } finally {
      if (mountedRef.current) setIsExporting(false);
    }
  }, [where, orderBy]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set("search", query);
    if (dateFrom && dateFrom !== DEFAULT_FROM) params.set("from", dateFrom);
    if (dateTo && dateTo !== DEFAULT_TO) params.set("to", dateTo);
    if (cardFilter !== "all") params.set("card", cardFilter);
    if (typeFilter !== "all") params.set("type", typeFilter);
    const next = params.toString();
    const current = location.search.replace(/^\?/, "");
    if (next !== current) {
      navigate(next ? `/attendees/list?${next}` : "/attendees/list", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, dateFrom, dateTo, cardFilter, typeFilter]);

  // Count matching the active card toggle; drives pagination and the "x–y of N" header.
  const total =
    cardFilter === "with" ? withCardCount : cardFilter === "without" ? allCount - withCardCount : allCount;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const onSortChange = useCallback((field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "id" || field === "created_at" ? "desc" : "asc");
    }
  }, [sortField]);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Breadcrumbs />
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setDateFrom(DEFAULT_FROM);
                setDateTo(DEFAULT_TO);
              }}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
            <SecondaryButton onClick={handleExport} isDisabled={isExporting || total === 0}>
              {isExporting ? "Exporting…" : "Export to CSV"}
            </SecondaryButton>
            {editCreatePermission && (
              <PrimaryButton onClick={() => navigate(`/attendees/create`)}>Create</PrimaryButton>
            )}
          </div>
        </div>
      </div>

      <AttendeesTable
        attendees={attendees}
        query={query}
        onQueryChange={setQuery}
        editPermission={editCreatePermission}
        cardFilter={cardFilter}
        onCardFilterChange={setCardFilter}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        allCount={allCount}
        withCardCount={withCardCount}
        onBulkSetInactive={handleBulkSetInactive}
        bulkPending={bulkPending}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={onSortChange}
        page={page}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
      />
    </div>
  );
};
